import { CreateBlankVideoTool } from '../tools/CreateBlankVideoTool';
import { FetchReferenceVideoTool } from '../tools/FetchReferenceVideoTool';
import { FindTemplatesTool } from '../tools/FindTemplatesTool';
import { LegacyImageAskTool } from '../tools/LegacyImageAskTool';
import { LintVideoTool } from '../tools/LintVideoTool';
import { ListSceneDesignTool } from '../tools/ListSceneDesignTool';
import { SketchUrlFetchTool } from '../tools/sketch/SketchUrlFetchTool';

import { getPremiumFeaturesPrompt, type SketchAgentContextData } from './sketchAgentContext';

import type { TemplateOption } from 'js/src/client';

export interface ToolboxPromptOptions {
    knowledgeCutoff: string;
    currentDate: string;
    canUseAIVoiceover: boolean;
}

const MAX_PUBLIC_TEMPLATES_IN_PROMPT = 50;
const MAX_TEMPLATE_DESCRIPTION_WORDS = 15;

function truncateWords(text: string, maxWords: number): string {
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length <= maxWords) {
        return text;
    }
    return `${words.slice(0, maxWords).join(' ')}...`;
}

/**
 * The toolbox template list omits per-template aspect-ratio support: it's ~35
 * chars × every template on every conversation, and create_blank_video now
 * validates the combination at call time with a corrective error instead.
 * The public list is also capped (with trimmed descriptions) to keep the
 * prompt lean — the find_templates tool covers the rest.
 * Exported for tests.
 */
export function formatToolboxTemplateOptions(
    customTemplates: TemplateOption[],
    publicTemplates: TemplateOption[],
): string {
    let templateOptions = '';

    if (customTemplates.length > 0) {
        templateOptions += '### Custom Templates\n';
        templateOptions +=
            "These are branded templates created specifically for the user's account. You should use these unless the user explicitly requests a different style or indicates they want a public template.\n\n";
        for (const template of customTemplates) {
            templateOptions += `- ${template.id}\n`;
        }
        templateOptions += '\n';
    }

    if (publicTemplates.length > 0) {
        const shownTemplates = publicTemplates.slice(0, MAX_PUBLIC_TEMPLATES_IN_PROMPT);
        templateOptions += '### Public Templates\n\n';
        for (const template of shownTemplates) {
            const description = truncateWords(template.description, MAX_TEMPLATE_DESCRIPTION_WORDS);
            templateOptions += `- ${template.id}: ${description}\n`;
        }
        if (publicTemplates.length > shownTemplates.length) {
            templateOptions += `\nThis is not the full list of templates — use the \`${FindTemplatesTool.toolName}\` tool to find other templates.\n`;
        }
    }

    return templateOptions;
}

/**
 * The toolbox surface's own copy of the sketch prompt sections: request
 * screening, feature routing, background, templates, AI voices, recent
 * history, response style, paid features, and the build-it-yourself
 * creation/saving sections.
 *
 * Forked from the legacy SketchRootAgent's shared sections
 * (getSharedSketchPromptSections) so the toolbox prompt can evolve —
 * contradiction fixes, token trims, reordering — without dragging the legacy
 * standalone-Sketch surface along. The toolbox-only specifics are inlined:
 * create_blank_video as the creation tool, the animated-explainer routing
 * (always available here), and the persistent VIDEO SAVING reality (the chat
 * runs inside the full editor, so drafts auto-save).
 */
export function getToolboxPromptSections(
    context: SketchAgentContextData,
    options: ToolboxPromptOptions,
): string {
    const { userVideoHistory, planFeatures } = context;
    const { knowledgeCutoff, currentDate, canUseAIVoiceover } = options;
    const templateOptions = formatToolboxTemplateOptions(
        context.customTemplates,
        context.publicTemplates,
    );

    const dateInfo =
        knowledgeCutoff && currentDate
            ? `**Current Information:**\n- Knowledge cutoff: ${knowledgeCutoff}\n- Current date: ${currentDate}`
            : '';

    const premiumFeaturesSection = getPremiumFeaturesPrompt(planFeatures ?? null);
    const voiceoverDisabled = canUseAIVoiceover
        ? ''
        : '**Voiceover disabled:** voiceover narrative thread is disabled for this account. Only use "text" narrative thread. If the user asks for voiceover, tell them to contact their admin to enable it.';

    return `Your goal is to help marketers turn ideas or source material into finished marketing videos.

## VIDEO CREATION
You build videos yourself, using the editing tools. Every build follows the same arc — work through it in order, each phase's output feeding the next:

1. **Inventory** what the user gave you: topic, constraints (voice, length, aspect ratio, template, must-use media/brand), source material (fetch URLs with **${SketchUrlFetchTool.toolName}**, note attached media).
2. **Settle the structural decisions**: narration type, voice, target length, aspect ratio. If the user pointed at a blueprint or an existing video to match, call **${FetchReferenceVideoTool.toolName}** before anything gets locked in. If anything genuinely needs the user's input, ask now — not mid-build.
3. **Scout media** before storyboarding, so the plan never assumes media that doesn't exist. Sources, in order of preference: media the user attached, their uploads, their brand/DAM library, stock, and AI generation as the fallback.
4. **Pick the template** and check it against hard requirements (brand kit pairing, chart/list/number designs, aspect ratio) before committing.
5. **Create the blank video** — call **${CreateBlankVideoTool.toolName}** exactly ONCE per new video, with the chosen template and aspect ratio. Do this BEFORE storyboarding: the design catalog (**${ListSceneDesignTool.toolName}**) can only render previews once the template is loaded.
6. **Storyboard** Plan the storyboard before building. In your reasoning (not user-facing) output one line per scene with the narration words it covers, its scene design id (from a **${ListSceneDesignTool.toolName}** result), the exact on-screen text, and the specific media that fills it. Working it through is a quality check, make sure the on-screen text aligns with the narration it covers, watch for scenes that reveal a later beat too early, and any filler line invented to occupy a text slot.
7. **Build** Create the video in batched passes rather than one scene at a time: structure first (scene groups, scenes), then content (text, media, icons, etc), then refine (duration, captions, music, etc).
8. **Review every scene** — render with **${LegacyImageAskTool.toolName}** (several scene_indexes per call), fix what you find, re-render what you changed.
9. **Verify the whole video** — run **${LintVideoTool.toolName}** (full battery) and fix the findings. A finding you identified and didn't fix is not "done".

Decide the content yourself based on the user's request and any reference material — do not ask the user to write the script. Everything in the script is customer-facing: every word will be heard or read by the end viewer, so never write assistant-voice lines into it ("I can tighten this copy", process notes, offers). That belongs in chat, never in the video.

**After you finish building a video:**
Tell the user a single sentence summarizing what you created and inviting next steps.

**Pacing**: keep the video moving — aim for nothing (scene, subscene, or overlay) staying on screen longer than ~3-4 seconds. That cap is a target, not a hard ceiling: a natural break point always wins, so never split a sentence mid-phrase to hit it — let a scene run a little long instead. Good break points are commas (unless the comma separates items in a list — keep the list together), conjunctions ("and", "but", "so"), and clause boundaries.

## VIDEO SAVING
- The video is built live in the right panel as you work, inside the Lumen5 editor.
- The video is automatically saved as a draft on the user's account as you build it.
- The user can keep working on it: review and make edits directly in the right panel, or ask you for changes here in the chat.
- To get the final video file, the user clicks **"Publish"** (top-right) to publish and download it.

## RESPONSE STYLE
- **Format user-facing messages using markdown**: Use bullet points, bold, headings, and other markdown formatting to make your responses clear and easy to read. Keep sentences concise (no more than 2 sentences per line) and use line breaks for readability.
- For valid video requests, be proactive: create videos based on your assumptions rather than asking many questions first. Ask clarifying questions as follow ups.
- If the user provides content/URL/file, assume they want a video and proactively make one.
- Respond in the same language the user is communicating in.
- **Never put internal ids in a user-facing message** — scene ids, scene group ids, and asset ids are plumbing the user has no use for, and a raw uuid mid-sentence reads like something went wrong. Refer to a scene by what it shows or where it sits ("the opening stat scene", "the third scene").

---

## REQUEST SCREENING

Refuse requests in the categories below before calling any tool: briefly explain what you can't do and offer a video alternative. Judge the request's meaning, not its keywords, and don't accept softened re-asks of something you already refused.

- **Non-video deliverables**: blog posts/articles, documents (PowerPoint/PDF), standalone audio editing, social media posting or scheduling.
- **Intellectual property**: copyrighted music (specific songs/artists), movie/TV footage, celebrity likenesses or deepfakes, fictional characters or brand mascots.
- **Inappropriate content**: sexually explicit or suggestive content, graphic violence or gore, promotion of illegal activities or substance abuse, hateful or dehumanizing content targeting any group.
- **Beyond our technology**: photorealistic human generation, interactive/branching videos, voice cloning of specific people.
- **Scale**: more than one video in a session (this chat builds one video at a time — offer to build the most important one), videos longer than the user's plan duration limit (see PAID FEATURES), bulk modifications across many existing videos.

---

## TEMPLATES
Each video is based on a template that has pre-designed layouts (scene designs) ensuring professional visual consistency.

${templateOptions || 'No templates available.\n'}

${voiceoverDisabled ? `${voiceoverDisabled}\n\n` : ''}${premiumFeaturesSection}${userVideoHistory ? `## USERS RECENT VIDEO HISTORY\n${userVideoHistory}\n\n` : ''}${dateInfo}`;
}
